import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial, trialSwap } from '../contexts/TrialContext';
import api from '../lib/api';
import {
  Check, Loader2, ArrowRight, Sparkles, PlayCircle,
  Zap, GraduationCap, Monitor, ShieldCheck, Clock4, Target,
  ChevronDown, Star,
} from 'lucide-react';
import SEO from '../components/SEO';
import { track } from '../lib/analytics';
import { startProCheckout } from '../lib/checkout';

/**
 * /pricing — iter 63b — intro video on top + Designjoy-inspired pricing
 * card below + Why-Us objection block + FAQ accordion. Mobile-responsive
 * with single-column collapse at <768px. Video URL is admin-editable
 * (site_config.pricing.video_url) and supports Loom / YouTube / Vimeo.
 *
 * Preserves the existing Stripe checkout flow exactly (POST
 * /api/payments/create-checkout with plan_id="pro"). Preserves data-testid
 * hooks the testing agent relies on (pricing-cta-pro, pricing-inline-free-btn,
 * pricing-card, pricing-amount, etc).
 */

// Normalise share URLs from Loom / YouTube / Vimeo to their /embed/ form.
// Required because share URLs return X-Frame-Options:deny on iframes.
function normalizeEmbedUrl(raw) {
  if (!raw) return '';
  const url = String(raw).trim();
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/(?:^|\/\/)(?:www\.)?vimeo\.com\/(?!video\/)(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

const FALLBACK = {
  title: 'One payment,',
  title_italic: 'three months of everything.',
  subtitle: 'Everything you need to crush CELPIP — graded by AI, designed by experts.',
  price: '296',
  original_price: '447',
  currency_symbol: '$',
  period: 'for 3 months',
  badge: 'Limited-Time Launch Price',
  cta: 'Join today',
  video_url: '',
  video_caption: 'Watch how CELPIPMax helps you score 9+ — straight from the founder.',
  features: [
    'All 3,000+ practice questions',
    'All 50+ full mock exams',
    'AI Writing & Speaking scoring',
    'Detailed explanations + samples',
    'Full performance dashboard',
    'All courses & video lectures',
    'Priority support',
  ],
  perks: ['Cancel anytime', 'No commitment'],
  free_features: [
    'Limited questions (Part 1–2 only)',
    '1 free mock exam',
    'Basic explanations',
  ],
  why_us: [
    { title: 'Instant AI Scoring',      body: "Writing & speaking results in seconds — no more waiting days for your tutor's feedback." },
    { title: 'Expert-built Curriculum', body: 'Every question, rubric and sample answer is written by certified CELPIP raters and Canadian-immigration coaches.' },
    { title: 'Built for Real Test Day', body: 'Our mocks mirror the official CELPIP UI pixel-for-pixel so test day feels familiar — not foreign.' },
    { title: 'Money-back Confidence',   body: 'Cancel any time inside one click. No emails. No contracts. No friction.' },
    { title: 'Study Anytime, Anywhere', body: 'Fully online. Works on laptop, tablet and phone. Pick up exactly where you left off.' },
    { title: 'Proven Score Strategies', body: 'Step-by-step plans built from the official Target-9 study pack — the same rubric the test makers use.' },
  ],
  faq: [
    { q: 'How is this different from a private CELPIP tutor?',
      a: "Private tutors charge $50-$100 per hour and you wait days for feedback. CELPIPMax gives you unlimited practice, AI-graded writing & speaking in seconds, AND access to expert mentors — all for the price of one tutoring session per month." },
    { q: 'What if my test is in a week?',
      a: "You can do every full mock test on day one. Most students see a 1-2 CLB jump after focused practice on the AI-graded mocks." },
    { q: 'Does it cover all 4 modules — Listening, Reading, Writing, Speaking?',
      a: "Yes. All four CELPIP modules are covered, with the AI scorer specifically calibrated for Writing and Speaking using the official Target-9 rubric." },
    { q: 'What happens after my 3 months end?',
      a: "Your access simply ends — no auto-renewal, no surprise charges, nothing to cancel. If you need more time, just grab another 3-month pass whenever you're ready." },
    { q: 'What if I\u2019m not happy?',
      a: "We offer a no-questions-asked refund inside the first 7 days. Email support@celpipmax.com and you'll get a full refund." },
    { q: 'Do you offer promo codes?',
      a: "Yes. Have a code from a referral? Enter it on the Stripe checkout screen — the discount applies instantly." },
  ],
};

const WHY_ICONS = [Zap, GraduationCap, Monitor, ShieldCheck, Clock4, Target];

function VideoBlock({ url, caption }) {
  const embed = normalizeEmbedUrl(url);
  return (
    <section
      className="max-w-5xl mx-auto mb-14 sm:mb-20"
      data-testid="pricing-video-section"
    >
      <div
        className="relative w-full overflow-hidden rounded-[20px] sm:rounded-[28px] bg-slate-900 ring-1 ring-slate-900/10 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.5)]"
        style={{ aspectRatio: '16 / 9' }}
      >
        {embed ? (
          <iframe
            src={embed}
            title="CELPIPMax — Introduction"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full"
            data-testid="pricing-video-iframe"
          />
        ) : (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-white/70 p-6 text-center"
            data-testid="pricing-video-empty"
          >
            <PlayCircle size={56} className="mb-3 opacity-50" />
            <p className="text-sm sm:text-base font-medium">Intro video coming soon.</p>
            <p className="text-xs text-white/40 mt-1">Admin can add a Loom / YouTube / Vimeo URL in /admin/pricing.</p>
          </div>
        )}
      </div>
      {caption && (
        <p
          className="text-center text-xs sm:text-[13px] text-neutral-500 mt-4 max-w-2xl mx-auto"
          data-testid="pricing-video-caption"
        >
          {caption}
        </p>
      )}
    </section>
  );
}

function FAQItem({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-white/60 backdrop-blur transition hover:border-neutral-300"
      data-testid={`pricing-faq-item-${idx}`}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
        data-testid={`pricing-faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="text-[15px] sm:text-base font-semibold text-neutral-900 leading-snug">{item.q}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-neutral-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p
            className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-neutral-600 leading-relaxed"
            data-testid={`pricing-faq-answer-${idx}`}
          >
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [config, setConfig] = useState(FALLBACK);

  useEffect(() => {
    track('view_pricing');
    api.get('/api/pricing/config').then(({ data }) => setConfig({ ...FALLBACK, ...(data || {}) })).catch(() => {});
  }, []);

  const handleSubscribe = async () => {
    track('cta_click', { location: 'pricing', label: 'subscribe' });
    // Logged-out users go create an account first, carrying the "pro" intent so
    // signup sends them straight to Stripe checkout (no detour through pricing).
    if (!user) { track('signup_intent', { source: 'pricing' }); navigate('/signup?intent=pro'); return; }
    setLoading(true);
    setCheckoutUrl(null);
    try {
      const url = await startProCheckout(config.price || FALLBACK.price);
      if (!url) { setLoading(false); }
    } catch {
      setLoading(false);
    }
  };

  // Entry tier: Free account → onboarding → Start Here; in Trial mode (Admin → Free vs Trial) → the trial checkout first.
  const { trialMode, entryLabel, price: trialPrice, hours: trialHours } = useTrial();
  const handleStartFree = () => {
    track('cta_click', { location: 'pricing', label: trialMode ? 'start_trial' : 'start_free' });
    if (!user) { navigate('/signup?intent=free'); return; }
    if (trialMode && user.trial?.status === 'unpaid') { navigate('/checkout?plan=trial'); return; }
    navigate(user.onboarding_completed === false ? '/onboarding' : '/dashboard');
  };

  const features = (config.features && config.features.length) ? config.features : FALLBACK.features;
  const half = Math.ceil(features.length / 2);
  const colA = features.slice(0, half);
  const colB = features.slice(half);
  const isCurrent = user?.subscription_status && user.subscription_status !== 'free';
  const whyUs = (config.why_us && config.why_us.length) ? config.why_us : FALLBACK.why_us;
  const faq = (config.faq && config.faq.length) ? config.faq : FALLBACK.faq;

  return (
    <div className="min-h-screen bg-[#f5f1eb]">
      <SEO title="Pricing" description="One payment, three months of everything — $296 for 3 months. Everything you need to ace your CELPIP exam." path="/pricing" />
      <Navbar />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-6 sm:pt-10 pb-16" data-testid="pricing-page">

        {/* ─── HERO ─── */}
        <div className="text-center mb-8 sm:mb-10">
          <span
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 text-[11px] font-semibold tracking-wide"
            data-testid="pricing-eyebrow"
          >
            <Sparkles size={12} />
            {config.badge || FALLBACK.badge}
          </span>
        </div>

        <h1
          className="text-center leading-[1.05] mb-4 sm:mb-5 text-neutral-900 tracking-tight"
          data-testid="pricing-title"
          style={{ fontFamily: '"Instrument Serif", "Playfair Display", Georgia, serif' }}
        >
          <span className="block text-4xl sm:text-5xl lg:text-6xl font-normal">{config.title}</span>
          <span className="block text-4xl sm:text-5xl lg:text-6xl italic font-normal text-neutral-700">{config.title_italic}</span>
        </h1>

        <p
          className="text-center text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto mb-10 sm:mb-12 px-2"
          data-testid="pricing-subtitle"
        >
          {config.subtitle || FALLBACK.subtitle}
        </p>

        {/* ─── VIDEO BLOCK (top) ─── */}
        <VideoBlock url={config.video_url} caption={config.video_caption} />

        {/* ─── PRICING CARD ─── */}
        <section
          className="relative max-w-4xl mx-auto p-6 sm:p-10 rounded-[24px] sm:rounded-[32px] text-white shadow-[0_30px_80px_-25px_rgba(15,23,42,0.55)]"
          style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}
          data-testid="pricing-card"
        >
          {/* corner CANCEL ANYTIME pill */}
          <span
            className="hidden sm:inline-block absolute top-6 right-6 text-[10px] tracking-[0.22em] font-semibold border border-white/25 px-3 py-1.5 rounded-full text-white/85"
            data-testid="pricing-cancel-anytime"
          >
            NO AUTO-RENEWAL
          </span>

          <div className="text-[10px] tracking-[0.28em] font-semibold text-amber-300 mb-2">CELPIPMAX PRO</div>
          <h2 className="text-2xl sm:text-4xl font-semibold tracking-tight mb-1">Everything you need to ace it.</h2>
          <p className="text-sm sm:text-[15px] text-white/60 mb-6 sm:mb-8 max-w-xl">
            One plan. All four modules. AI scoring, mocks, courses & priority support — included.
          </p>

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-7" data-testid="pricing-amount-block">
            <span className="text-5xl sm:text-7xl font-bold tracking-tighter leading-none" data-testid="pricing-amount">
              {config.currency_symbol || '$'}{config.price || FALLBACK.price}
            </span>
            <span className="text-base sm:text-lg text-white/70 self-end pb-1.5">{config.period || FALLBACK.period}</span>
            {config.original_price && (
              <span className="text-lg sm:text-xl text-white/40 line-through self-end pb-2 ml-1" data-testid="pricing-original">
                {config.currency_symbol || '$'}{config.original_price}
              </span>
            )}
          </div>

          {/* INCLUDED features */}
          <div className="rounded-[18px] bg-white/5 border border-white/10 p-4 sm:p-5 mb-6" data-testid="pricing-included">
            <div className="inline-block px-2.5 py-1 mb-3 rounded-md text-[10px] tracking-[0.22em] font-semibold bg-white/8 border border-white/15 text-white/85">
              INCLUDED
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {colA.map((f, i) => (
                <div key={`a-${i}`} className="flex items-start gap-2.5 text-[13.5px] sm:text-sm text-white/90">
                  <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>{f}</span>
                </div>
              ))}
              {colB.map((f, i) => (
                <div key={`b-${i}`} className="flex items-start gap-2.5 text-[13.5px] sm:text-sm text-white/90">
                  <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Primary CTA */}
          <button
            onClick={handleSubscribe}
            disabled={loading || isCurrent}
            className="group w-full flex items-center justify-between gap-3 px-2 py-2 rounded-[16px] bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-neutral-900 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_12px_28px_-8px_rgba(245,158,11,0.55)] hover:shadow-[0_18px_36px_-10px_rgba(245,158,11,0.7)] hover:-translate-y-0.5 min-h-[56px]"
            data-testid="pricing-cta-pro"
          >
            <span className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-[12px] bg-orange-500 flex items-center justify-center text-white shrink-0">
                <ArrowRight size={20} className="transition group-hover:translate-x-0.5" />
              </span>
              <span className="font-semibold tracking-tight text-[15px] sm:text-base">
                {loading
                  ? 'Opening checkout…'
                  : isCurrent
                    ? "You're already a Pro member"
                    : (config.cta || FALLBACK.cta)
                }
              </span>
            </span>
            {loading && <Loader2 size={18} className="mr-3 animate-spin" />}
          </button>

          {/* Secondary "Or start free" — prominent so free-trial users never get stuck */}
          <div className="mt-4" data-testid="pricing-inline-free-cta-block">
            <button
              type="button"
              onClick={handleStartFree}
              className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-[14px] border border-white/35 bg-white/[0.06] text-white font-semibold text-sm hover:bg-white/[0.12] hover:border-white/60 transition min-h-[52px]"
              data-testid="pricing-inline-free-btn"
            >
              {trialMode ? `Start the ${trialPrice} trial — ${trialHours} hours of access` : 'Start free - no credit card required'}
              <ArrowRight size={16} />
            </button>
            <p className="mt-2 text-center text-[12px] text-white/50">
              {trialMode ? `One-time ${trialPrice}. Try the trial material for ${trialHours} hours, upgrade to Pro anytime.` : 'Create a free account & practise instantly. Upgrade to Pro anytime.'}
            </p>
          </div>

          {/* Perks */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 text-[12px] text-white/55">
            {(config.perks || FALLBACK.perks).map((p, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-white/40" />
                {p}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-white/40" />
              Promo codes accepted at checkout
            </span>
          </div>
        </section>

        {/* Popup-blocker fallback */}
        {checkoutUrl && (
          <div className="max-w-3xl mx-auto mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-center" data-testid="checkout-fallback">
            <p className="text-sm text-amber-900 mb-2">If the checkout didn't open automatically:</p>
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer"
              className="inline-block px-6 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition text-sm">
              Click here to complete payment
            </a>
          </div>
        )}

        {/* Free tier footnote */}
        <div className="mt-6 text-center" data-testid="pricing-free-cta-block">
          <p className="text-xs text-neutral-500 max-w-md mx-auto leading-relaxed" data-testid="pricing-free-tier-summary">
            <span className="font-semibold text-neutral-600">{trialMode ? `${entryLabel} (${trialPrice} · ${trialHours} h) includes:` : 'Free plan includes:'}</span>{' '}
            {(config.free_features || FALLBACK.free_features).map((f) => trialSwap(f, entryLabel, trialMode)).join(' · ')}
          </p>
        </div>

        {/* ─── TRUST / SOCIAL PROOF strip ─── */}
        <section
          className="max-w-3xl mx-auto mt-14 sm:mt-20 flex flex-col items-center gap-3 text-center"
          data-testid="pricing-trust-strip"
        >
          <div className="flex items-center gap-1 text-amber-500" aria-label="Five star rating">
            {[0, 1, 2, 3, 4].map(i => <Star key={i} size={18} fill="currentColor" stroke="none" />)}
          </div>
          <p className="text-sm sm:text-[15px] text-neutral-700">
            Trusted by <span className="font-semibold text-neutral-900">10,000+</span> CELPIP test-takers across Canada, India, the Philippines & beyond.
          </p>
        </section>

        {/* ─── WHY CELPIPMax ─── */}
        <section className="mt-16 sm:mt-24" data-testid="pricing-why-section">
          <div className="text-center mb-10 sm:mb-12">
            <span className="text-[11px] tracking-[0.32em] text-neutral-500 font-semibold">WHY CELPIPMAX</span>
            <h2
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-neutral-900 leading-tight tracking-tight"
              style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
            >
              Better than another <em className="italic text-neutral-700">expensive coaching</em>.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-6xl mx-auto">
            {whyUs.map((item, idx) => {
              const Icon = WHY_ICONS[idx % WHY_ICONS.length];
              return (
                <div
                  key={idx}
                  className="group relative p-5 sm:p-6 rounded-2xl bg-white border border-neutral-200 hover:border-neutral-300 hover:shadow-[0_18px_40px_-20px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition"
                  data-testid={`pricing-why-card-${idx}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-4 group-hover:bg-amber-500 group-hover:text-white transition">
                    <Icon size={20} strokeWidth={2.2} />
                  </div>
                  <h3 className="text-[15px] sm:text-base font-semibold text-neutral-900 mb-1.5">{item.title}</h3>
                  <p className="text-[13px] sm:text-sm text-neutral-600 leading-relaxed">{item.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="mt-16 sm:mt-24" data-testid="pricing-faq-section">
          <div className="text-center mb-8 sm:mb-10">
            <span className="text-[11px] tracking-[0.32em] text-neutral-500 font-semibold">QUESTIONS</span>
            <h2
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-neutral-900 leading-tight tracking-tight"
              style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
            >
              <em className="italic text-neutral-700">Frequently</em> asked.
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faq.map((item, i) => (
              <FAQItem key={i} item={item} idx={i} />
            ))}
          </div>
        </section>

        {/* ─── FOOTER CTA ─── */}
        <section className="mt-16 sm:mt-24 mb-4" data-testid="pricing-footer-cta">
          <div
            className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-14 rounded-[24px] sm:rounded-[32px] text-center text-white shadow-[0_30px_80px_-25px_rgba(15,23,42,0.55)]"
            style={{ background: 'linear-gradient(135deg,#0b5cad 0%,#0f172a 60%,#1e293b 100%)' }}
          >
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl mb-3"
              style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
            >
              Ready to score <em className="italic">9+</em>?
            </h2>
            <p className="text-sm sm:text-base text-white/70 max-w-xl mx-auto mb-7">
              Start practising in under a minute. Cancel anytime.
            </p>
            <button
              onClick={handleSubscribe}
              disabled={loading || isCurrent}
              className="inline-flex items-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-neutral-900 font-semibold text-[15px] sm:text-base transition disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_18px_36px_-10px_rgba(245,158,11,0.7)] hover:-translate-y-0.5"
              data-testid="pricing-footer-cta-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              {isCurrent ? "You're already a Pro member" : (config.cta || FALLBACK.cta)}
            </button>
            {!isCurrent && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleStartFree}
                  className="text-[13px] text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white/70 transition"
                  data-testid="pricing-footer-free-btn"
                >
                  {trialMode ? `or start the ${trialPrice} trial →` : 'or start free — no credit card required →'}
                </button>
              </div>
            )}
            <p className="mt-5 text-[12px] text-white/45">Promo codes accepted at checkout · 7-day refund window</p>
          </div>
        </section>

      </div>
    </div>
  );
}
